import { useState, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface CronBuilderProps {
  value: string;
  onChange: (cron: string) => void;
}

export default function CronBuilder({ value, onChange }: CronBuilderProps) {
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('0');
  const [day, setDay] = useState('*');
  const [month, setMonth] = useState('*');
  const [weekday, setWeekday] = useState('*');
  const [preset, setPreset] = useState('custom');

  const PRESETS = [
    { value: 'custom', label: 'Custom', cron: '' },
    { value: 'every_minute', label: 'Every minute', cron: '* * * * *' },
    { value: 'every_5_minutes', label: 'Every 5 minutes', cron: '*/5 * * * *' },
    { value: 'every_15_minutes', label: 'Every 15 minutes', cron: '*/15 * * * *' },
    { value: 'every_30_minutes', label: 'Every 30 minutes', cron: '*/30 * * * *' },
    { value: 'hourly', label: 'Every hour', cron: '0 * * * *' },
    { value: 'daily', label: 'Daily at midnight', cron: '0 0 * * *' },
    { value: 'daily_noon', label: 'Daily at noon', cron: '0 12 * * *' },
    { value: 'weekly', label: 'Weekly (Monday at midnight)', cron: '0 0 * * 1' },
    { value: 'monthly', label: 'Monthly (1st at midnight)', cron: '0 0 1 * *' },
  ];

  const WEEKDAYS = [
    { value: '0', label: 'Sunday' },
    { value: '1', label: 'Monday' },
    { value: '2', label: 'Tuesday' },
    { value: '3', label: 'Wednesday' },
    { value: '4', label: 'Thursday' },
    { value: '5', label: 'Friday' },
    { value: '6', label: 'Saturday' },
  ];

  useEffect(() => {
    // Parse incoming cron expression
    if (value) {
      const parts = value.split(' ');
      if (parts.length === 5) {
        setMinute(parts[0]);
        setHour(parts[1]);
        setDay(parts[2]);
        setMonth(parts[3]);
        setWeekday(parts[4]);

        // Check if matches a preset
        const matchingPreset = PRESETS.find((p) => p.cron === value);
        setPreset(matchingPreset?.value || 'custom');
      }
    }
  }, [value]);

  useEffect(() => {
    // Build cron expression from fields
    if (preset === 'custom') {
      const cronExpression = `${minute} ${hour} ${day} ${month} ${weekday}`;
      onChange(cronExpression);
    }
  }, [minute, hour, day, month, weekday, preset]);

  const handlePresetChange = (presetValue: string) => {
    setPreset(presetValue);
    const selectedPreset = PRESETS.find((p) => p.value === presetValue);
    if (selectedPreset && selectedPreset.cron) {
      onChange(selectedPreset.cron);
    }
  };

  const getHumanReadable = () => {
    const parts = [minute, hour, day, month, weekday];

    // Check for presets
    const matchingPreset = PRESETS.find((p) => p.cron === parts.join(' '));
    if (matchingPreset) {
      return matchingPreset.label;
    }

    // Build human-readable string
    let readable = 'Runs ';

    // Minute
    if (minute === '*') {
      readable += 'every minute';
    } else if (minute.startsWith('*/')) {
      readable += `every ${minute.substring(2)} minutes`;
    } else {
      readable += `at minute ${minute}`;
    }

    // Hour
    if (hour !== '*') {
      if (hour.startsWith('*/')) {
        readable += ` of every ${hour.substring(2)} hours`;
      } else {
        readable += ` past hour ${hour}`;
      }
    }

    // Day
    if (day !== '*') {
      if (day.startsWith('*/')) {
        readable += ` every ${day.substring(2)} days`;
      } else {
        readable += ` on day ${day}`;
      }
    }

    // Month
    if (month !== '*') {
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      readable += ` in ${months[parseInt(month) - 1] || month}`;
    }

    // Weekday
    if (weekday !== '*') {
      const weekdayLabel = WEEKDAYS.find((w) => w.value === weekday)?.label || weekday;
      readable += ` on ${weekdayLabel}`;
    }

    return readable;
  };

  return (
    <div className="space-y-4">
      {/* Preset Selection */}
      <div>
        <label className="block text-sm font-medium text-slate-300 mb-2">
          Presets
        </label>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          {PRESETS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => handlePresetChange(p.value)}
              className={`px-3 py-2 text-sm rounded transition-colors ${
                preset === p.value
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Fields */}
      {preset === 'custom' && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Minute
            </label>
            <input
              type="text"
              value={minute}
              onChange={(e) => setMinute(e.target.value)}
              placeholder="0-59, *, */"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">0-59</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Hour
            </label>
            <input
              type="text"
              value={hour}
              onChange={(e) => setHour(e.target.value)}
              placeholder="0-23, *, */"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">0-23</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Day
            </label>
            <input
              type="text"
              value={day}
              onChange={(e) => setDay(e.target.value)}
              placeholder="1-31, *, */"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">1-31</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Month
            </label>
            <input
              type="text"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              placeholder="1-12, *, */"
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-slate-500 mt-1">1-12</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Weekday
            </label>
            <select
              value={weekday}
              onChange={(e) => setWeekday(e.target.value)}
              className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="*">Any</option>
              {WEEKDAYS.map((w) => (
                <option key={w.value} value={w.value}>
                  {w.label}
                </option>
              ))}
            </select>
            <p className="text-xs text-slate-500 mt-1">0-6</p>
          </div>
        </div>
      )}

      {/* Human Readable Output */}
      <div className="p-4 bg-blue-900/30 border border-blue-700 rounded-lg">
        <div className="flex items-start gap-3">
          <Clock className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-blue-300 mb-1">Schedule:</p>
            <p className="text-blue-100">{getHumanReadable()}</p>
          </div>
        </div>
      </div>

      {/* Help Text */}
      <div className="text-xs text-slate-400 space-y-1">
        <p><strong>Wildcards:</strong> * = any value, */n = every n units (e.g., */5 = every 5)</p>
        <p><strong>Examples:</strong> "0 0 * * *" = daily at midnight, "*/15 * * * *" = every 15 minutes</p>
      </div>
    </div>
  );
}
